import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TableCell,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { FC, useState } from "react";
import { IGroupedItem } from "../../../types/orders";
import { Option } from "../../../types/core";
import useFarmers from "../../../services/useFarmers";
import useReplaceItem from "../../../services/useReplaceItem";
import useCancelGroupedItems from "../../../services/useUpdateGroupedOrderItemsStatus";
import { Cancel as CancelIcon, CheckCircle } from "@mui/icons-material";

interface Props {
  fairId: number;
  groupedItem: IGroupedItem;
}

/**
 * Determina o estado do grupo de items.
 * - 'all_canceled': Todos cancelados
 * - 'some_canceled': Alguns cancelados
 * - 'none_canceled': Nenhum cancelado
 */
const getItemsState = (groupedItem: IGroupedItem) => {
  const allCanceled = groupedItem.items.every((item) => item.is_canceled);
  const someCanceled = groupedItem.items.some((item) => item.is_canceled);

  if (allCanceled) return "all_canceled";
  if (someCanceled) return "some_canceled";
  return "none_canceled";
};

const GroupedOrderItem: FC<Props> = ({ fairId, groupedItem }) => {
  const theme = useTheme();
  const { mutate: cancelGroupedItems } = useCancelGroupedItems();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const { data: farmers } = useFarmers();
  const [farmer, setFarmer] = useState<Option | null>(null);
  const [cost, setCost] = useState("");
  const { mutate: replaceItem } = useReplaceItem();

  const itemsState = getItemsState(groupedItem);

  const handleCancel = () => {
    setReasonDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    cancelGroupedItems({
      farmerOrderId: groupedItem.farmerOrder.id,
      productId: groupedItem.product.id,
      orderItemIds: groupedItem.items.map((item) => item.id),
      action: "cancel",
      reason: cancelReason || "Item não entregue pelo agricultor",
    });
    setReasonDialogOpen(false);
    setCancelReason("");
  };

  const handleUncancel = () => {
    cancelGroupedItems({
      farmerOrderId: groupedItem.farmerOrder.id,
      productId: groupedItem.product.id,
      orderItemIds: groupedItem.items.map((item) => item.id),
      action: "uncancel",
    });
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleNotSubstituteItem = () => {
    cancelGroupedItems({
      farmerOrderId: groupedItem.farmerOrder.id,
      productId: groupedItem.product.id,
      orderItemIds: groupedItem.items.map((item) => item.id),
      action: "cancel",
      reason: "Item não entregue - sem substituição",
    });
    setDialogOpen(false);
  };

  const handleSubstituteItem = () => {
    if (farmer && farmer.value) {
      replaceItem({
        cost,
        fair: fairId,
        farmer: farmer.value,
        orderItems: groupedItem.items.map((item) => item.id),
        product: groupedItem.product.id,
      });
    }
    setDialogOpen(false);
  };

  return (
    <TableRow
      sx={{
        backgroundColor:
          itemsState === "all_canceled"
            ? theme.palette.error.light
            : itemsState === "some_canceled"
              ? theme.palette.warning.light
              : "transparent",
      }}
    >
      <TableCell>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {itemsState === "all_canceled" ? (
            <>
              <Chip
                icon={<CancelIcon />}
                label="Todos cancelados"
                color="error"
                size="small"
              />
              <Button size="small" variant="outlined" onClick={handleUncancel}>
                Descancelar
              </Button>
            </>
          ) : itemsState === "some_canceled" ? (
            <>
              <Chip
                icon={<CancelIcon />}
                label="Parcialmente cancelado"
                color="warning"
                size="small"
              />
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleCancel}
              >
                Cancelar restantes
              </Button>
            </>
          ) : (
            <>
              <Chip
                icon={<CheckCircle />}
                label="Ativo"
                color="success"
                size="small"
              />
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleCancel}
              >
                Cancelar
              </Button>
            </>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">{groupedItem.amount}</TableCell>
      <TableCell>
        {groupedItem.product.base_product.name}
        {groupedItem.product.base_product.unit
          ? ` (${groupedItem.product.base_product.unit})`
          : ""}
      </TableCell>
      <TableCell align="right">
        {(groupedItem.amount * groupedItem.product.cost).toFixed(2)}
      </TableCell>

      {/* Dialog para substituir item */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        PaperProps={{
          sx: { overflow: "visible" },
        }}
      >
        <DialogTitle>Alterar item do pedido</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Deseja alterar a origem do produto?
          </DialogContentText>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Autocomplete
              value={farmer}
              onChange={(e, newValue) => {
                setFarmer(newValue);
              }}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              disablePortal
              options={
                farmers
                  ? farmers.map((farmer) => ({
                      label: farmer.name,
                      value: farmer.id,
                    }))
                  : []
              }
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label="Buscar produtor" />
              )}
            />
            <TextField
              label="Custo"
              onChange={(e) => {
                setCost(e.target.value);
              }}
              value={cost}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNotSubstituteItem}>Não substituir item</Button>
          <Button onClick={handleSubstituteItem}>Substituir</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para motivo de cancelamento */}
      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
      >
        <DialogTitle>Cancelar items</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Informe o motivo do cancelamento (opcional):
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Motivo"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Ex: Agricultor não conseguiu entregar"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
          >
            Confirmar cancelamento
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
};

export default GroupedOrderItem;
